import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";

export default function TopBar({ title, subtitle, actions }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setResults(data.results || []);
        setOpen(true);
      } catch (e) { console.error("Search failed", e); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="border-b border-surface-3 bg-[#0B0D12] sticky top-0 z-30" data-testid="topbar">
      <div className="flex items-center gap-6 px-8 py-4">
        <div className="min-w-0 flex-1">
          <div className="font-display text-3xl tracking-wider text-white uppercase leading-none" data-testid="page-title">
            {title}
          </div>
          {subtitle && (
            <div className="text-xs font-condensed uppercase tracking-[0.28em] text-amber mt-1.5">
              {subtitle}
            </div>
          )}
        </div>

        {/* Universal Search */}
        <div ref={boxRef} className="relative w-[380px] max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="search-input"
            className="w-full bg-surface border border-surface-3 pl-10 pr-9 py-2 text-sm placeholder:text-slate-500 text-white focus:outline-none focus:border-amber transition-colors"
            placeholder="Search everything: drills, docs, plays, staff…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q && setOpen(true)}
          />
          {q && (
            <button onClick={() => { setQ(""); setResults([]); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
          {open && results.length > 0 && (
            <div className="absolute mt-1 left-0 right-0 bg-surface border border-surface-3 max-h-[420px] overflow-y-auto z-40 shadow-2xl" data-testid="search-results">
              {results.map((r) => (
                <button
                  key={`${r.type}-${r.id || r.path}`}
                  onClick={() => { nav(r.path); setOpen(false); setQ(""); }}
                  className="w-full text-left px-4 py-3 border-b border-surface-3 last:border-b-0 hover:bg-surface-2 transition-colors"
                >
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-amber">{r.module}</div>
                  <div className="font-semibold text-sm mt-0.5">{r.title}</div>
                  <div className="text-xs text-slate-400 mt-1 line-clamp-1">{r.snippet}</div>
                </button>
              ))}
            </div>
          )}
          {open && q.length >= 2 && results.length === 0 && (
            <div className="absolute mt-1 left-0 right-0 bg-surface border border-surface-3 px-4 py-3 text-sm text-slate-400" data-testid="search-empty">
              No matches for "{q}"
            </div>
          )}
        </div>

        {actions}
      </div>
    </header>
  );
}
